import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { UntilDestroy } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { ToasterService } from '@nx-ratenow/core/feature-notifications';
import { CasesService } from '@nx-ratenow/ctl/cases/data-access';
import { NotificationsService } from '@nx-ratenow/shared/data-access-utils';
import { PopoverService } from '@nx-ratenow/shared/feature-popover';
import { ConfirmModalComponent, ConfirmModalData, EditNotificationsComponent } from '@nx-ratenow/shared/modals';
import { ActionType, PersonNotificationDto } from '@nx-ratenow/shared/models';
import { shakeAnimation } from 'angular-animations';
import { Observable, Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { NotificationsFacade } from '../+state/notifications/notifications.facade';
import { ToolbarNotificationsDropdownComponent } from './toolbar-notifications-dropdown/toolbar-notifications-dropdown.component';

@UntilDestroy()
@Component({
  standalone: false,
  selector: 'nx-ratenow-toolbar-notifications',
  templateUrl: './toolbar-notifications.component.html',
  styleUrls: ['./toolbar-notifications.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [shakeAnimation({ duration: 2000, translate: '5px' })]
})
export class ToolbarNotificationsComponent implements OnInit, OnDestroy {
  @ViewChildren('originRef', { read: ElementRef })
  originRef!: QueryList<ElementRef>;

  ngUnsubscribe$: Subject<ToolbarNotificationsComponent> = new Subject();

  notifications: PersonNotificationDto[] = [];
  scheduledNotifications: PersonNotificationDto[] = [];
  numNotifications$: Observable<number> = this.notificationsFacade.counter$;

  dropdownOpen!: boolean;
  animState = false;

  constructor(
    private popover: PopoverService,
    private cd: ChangeDetectorRef,
    private notificationService: NotificationsService,
    private toasterService: ToasterService,
    private dialog: MatDialog,
    private casesService: CasesService,
    private notificationsFacade: NotificationsFacade,
    private router: Router,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.notificationsFacade.action$
      .pipe(
        filter((val) => val != undefined),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe((val) => {
        this.actionByType(val);
      });
  }

  ngOnDestroy() {
    this.ngUnsubscribe$.next(this);
    this.ngUnsubscribe$.complete();
  }

  showPopover() {
    this.dropdownOpen = true;
    this.cd.markForCheck();

    const popoverRef = this.popover.open({
      content: ToolbarNotificationsDropdownComponent,
      origin: this.originRef.first,
      offsetY: 12,
      position: [
        {
          originX: 'center',
          originY: 'top',
          overlayX: 'center',
          overlayY: 'bottom'
        },
        {
          originX: 'end',
          originY: 'bottom',
          overlayX: 'end',
          overlayY: 'top'
        }
      ]
    });

    popoverRef.afterClosed$.pipe(take(1)).subscribe(() => {
      this.dropdownOpen = false;
      this.cd.markForCheck();
    });
  }

  actionByType(object: { actionType: ActionType; notification: PersonNotificationDto }) {
    switch (object?.actionType) {
      case ActionType.DELETE: {
        this.deleteNotification(object.notification);
        break;
      }
      case ActionType.VIEWED: {
        this.changeNotificationViewed(object.notification);
        break;
      }
      case ActionType.CHANGE_DATE: {
        this.editNotification(object.notification, 'DATE');
        break;
      }
      case ActionType.CHANGE_MESSAGE: {
        this.editNotification(object.notification, 'MESSAGE');
        break;
      }
      case ActionType.SHOW: {
        this.openDialog(object.notification);
        break;
      }
      default: {
        break;
      }
    }
  }

  private changeNotificationViewed(notification: PersonNotificationDto): void {
    this.notificationService.changeViewedNotification(notification.id).subscribe({
      next: () => {
        this.notificationsFacade.getNotifications();
      },
      error: () => {
        this.toasterService.error('toolbar-notifications.error-editing');
      }
    });
  }

  private deleteNotification(notification: PersonNotificationDto): void {
    this.openConfirmationDialogAndExecute(
      {
        title: undefined,
        contentText: undefined
      },
      this.translateService.instant('toolbar-notifications.notification-deleted'),
      this.translateService.instant('toolbar-notifications.notification-deleted-error'),
      () => this.notificationService.deleteNotification(notification.id)
    );
  }

  private openConfirmationDialogAndExecute(
    dialogText: ConfirmModalData,
    successMessageKey: string,
    errorMessageKey: string,
    serviceFunction: () => Observable<any>,
    widthModal?: string
  ) {
    this.dialog
      .open(ConfirmModalComponent, {
        data: dialogText,
        autoFocus: false,
        width: widthModal
      })
      .afterClosed()
      .pipe(filter((val) => val))
      .subscribe(() => {
        serviceFunction()
          .pipe(take(1))
          .subscribe({
            next: () => {
              this.notificationsFacade.getNotifications();
              this.toasterService.success(successMessageKey);
            },
            error: () => {
              this.toasterService.error(errorMessageKey);
            }
          });
      });
  }

  private openDialog(notification: PersonNotificationDto) {
    this.casesService
      .getCaseById(notification.referenceId)
      .pipe(take(1), takeUntil(this.ngUnsubscribe$))
      .subscribe({
        next: (data) => {
          if (!notification.viewed) {
            this.notificationService.changeViewedNotification(notification.id).subscribe(() => {
              this.notificationsFacade.getNotifications();
              this.router.navigateByUrl('/case/' + data.id);
            });
          } else {
            this.router.navigateByUrl('/case/' + data.id);
          }
        },
        error: () => {
          this.toasterService.error('toolbar-notifications.error');
        }
      });
  }

  private editNotification(notification: PersonNotificationDto, modeEdit: string): void {
    this.dialog
      .open(EditNotificationsComponent, {
        data: { notification, modeEdit },
        autoFocus: false,
        width: '40vw'
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe((data) => {
        if (data) {
          if (data.modeEdit === 'DATE') {
            this.notificationService
              .changeScheduleNotification(notification.id, data.formValue.executeOn)
              .pipe(take(1))
              .subscribe({
                next: () => {
                  this.notificationsFacade.getNotifications();
                },
                error: () => {
                  this.toasterService.error('toolbar-notifications.notification-update-error');
                }
              });
          } else {
            this.notificationService
              .changeMessageNotification(notification.id, data.formValue.message)
              .pipe(take(1))
              .subscribe({
                next: () => {
                  this.notificationsFacade.getNotifications();
                },
                error: () => {
                  this.toasterService.error('toolbar-notifications.notification-update-error');
                }
              });
          }
        }
      });
  }
}
